import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  useTheme,
  styled,
  alpha,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  useMediaQuery,
  IconButton,
  Tooltip,
  Fab,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Card,
  CardContent,
  Slide,
  Zoom,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  MenuBook as MenuBookIcon,
  Article as ArticleIcon,
  Book as ChapterIcon,
  LibraryBooks as PartIcon,
  Description as SectionIcon,
  Link as LinkIcon,
  KeyboardArrowUp as ScrollTopIcon,
  ExpandLess,
  ExpandMore,
  Close as CloseIcon,
  Gavel as LawIcon,
  SwapHoriz as SwapIcon,
  Public as CountryIcon,
} from '@mui/icons-material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import TocRoundedIcon from '@mui/icons-material/TocRounded'
import { useParams, useNavigate } from 'react-router-dom'
import apiService from '../../services/ApiService'
import { useCountryLaw } from '../../context/CountryLawContext'

// Helper function to generate unique section IDs
const generateSectionId = (item) => {
  if (item.path) {
    return item.path.replace(/\//g, '-')
  }
  return `${item.type.toLowerCase()}-${item.number}`
}

const CopyLinkButton = ({ sectionId }) => {
  const [copied, setCopied] = useState(false)

  const handleCopyLink = (e) => {
    e.stopPropagation()
    const url = `${window.location.origin}${window.location.pathname}/${sectionId}`
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy link'} arrow>
      <IconButton
        size='small'
        onClick={handleCopyLink}
        sx={{
          color: copied ? '#22c55e' : '#9ca3af',
          '&:hover': {
            color: '#ffcf51',
            bgcolor: alpha('#ffcf51', 0.1),
          },
        }}
      >
        {copied ? (
          <CheckCircleRoundedIcon fontSize='small' />
        ) : (
          <LinkIcon fontSize='small' />
        )}
      </IconButton>
    </Tooltip>
  )
}

// Enhanced Law Selector Component
const LawSelector = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  },
}))

// Sleek styled components with modern design
const TableOfContents = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  padding: theme.spacing(3),
  maxHeight: 'calc(100vh - 100px)',
  overflowY: 'auto',
  borderRadius: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha('#ffcf51', 0.3),
    borderRadius: 2,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: alpha('#ffcf51', 0.5),
  },
}))

// Enhanced Law Selector Component with Country Selection
const LawSelectorComponent = ({
  availableLaws,
  countriesWithLaws,
  currentLawShortName,
  selectedCountry,
  onLawChange,
  onCountryChange,
  loading,
}) => {
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const currentLaw = useMemo(() => {
    return availableLaws.find(
      (law) =>
        law.lawShortName.toLowerCase() === currentLawShortName.toLowerCase()
    )
  }, [availableLaws, currentLawShortName])

  const currentCountry = useMemo(() => {
    return countriesWithLaws.find(
      (country) =>
        country.countryName.toLowerCase() === selectedCountry.toLowerCase()
    )
  }, [countriesWithLaws, selectedCountry])

  return (
    <LawSelector>
      {/* Country Selection */}
      {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1.5,
            marginRight: 1.5,
            color: 'white',
            background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
          }}
        >
          <CountryIcon fontSize='small' />
        </Box>
        <Typography
          variant={isMobileScreen ? 'subtitle1' : 'h6'}
          sx={{
            fontWeight: 700,
            color: '#1f2937',
            fontSize: isMobileScreen ? '1rem' : '1.1rem',
          }}
        >
          Select Country
        </Typography>
      </Box> */}

      <FormControl
        fullWidth
        variant='outlined'
        disabled={loading}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'white',
            '& fieldset': {
              borderColor: alpha('#e5e7eb', 0.8),
            },
            '&:hover fieldset': {
              borderColor: '#10b981',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#10b981',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root': {
            '&.Mui-focused': {
              color: '#10b981',
            },
          },
        }}
      >
        <InputLabel shrink={Boolean(selectedCountry)}>
          Choose Country
        </InputLabel>
        <Select
          value={selectedCountry || ''}
          onChange={(e) => onCountryChange(e.target.value)}
          label='Choose Country'
          renderValue={(value) => {
            const country = countriesWithLaws.find(
              (c) => c.countryName === value
            )
            return country ? country.countryName : value
          }}
          startAdornment={
            loading && (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ color: '#10b981' }} />
              </Box>
            )
          }
        >
          <MenuItem value='' disabled>
            Select a country
          </MenuItem>
          {countriesWithLaws.map((country) => (
            <MenuItem
              key={country.countrySlug || country.id || country.countryName}
              value={country.countryName}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: alpha('#10b981', 0.08),
                },
                '&.Mui-selected': {
                  bgcolor: alpha('#10b981', 0.12),
                  '&:hover': {
                    bgcolor: alpha('#10b981', 0.16),
                  },
                },
              }}
            >
              <Box>
                <Typography
                  variant='body1'
                  sx={{
                    fontWeight:
                      country.countryName === selectedCountry ? 600 : 400,
                    color: '#1f2937',
                  }}
                >
                  {country.countryName}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Law Selection */}
      {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1.5,
            marginRight: 1.5,
            color: 'white',
            background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
          }}
        >
          <LawIcon fontSize='small' />
        </Box>
        <Typography
          variant={isMobileScreen ? 'subtitle1' : 'h6'}
          sx={{
            fontWeight: 700,
            color: '#1f2937',
            fontSize: isMobileScreen ? '1rem' : '1.1rem',
          }}
        >
          Select Law
        </Typography>
      </Box> */}

      <FormControl
        fullWidth
        variant='outlined'
        disabled={loading || !selectedCountry || availableLaws.length === 0}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'white',
            '& fieldset': {
              borderColor: alpha('#e5e7eb', 0.8),
            },
            '&:hover fieldset': {
              borderColor: '#ffcf51',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#ffcf51',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root': {
            '&.Mui-focused': {
              color: '#ffcf51',
            },
          },
        }}
      >
        <InputLabel shrink={Boolean(currentLaw)}>Choose Law to View</InputLabel>
        <Select
          value={currentLawShortName || ''}
          onChange={(e) => onLawChange(e.target.value)}
          label='Choose Law to View'
          renderValue={(value) => {
            const law = availableLaws.find((l) => l.lawShortName === value)
            return law ? law.lawFullName : value
          }}
          startAdornment={
            loading && (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ color: '#ffcf51' }} />
              </Box>
            )
          }
        >
          <MenuItem value='' disabled>
            {!selectedCountry
              ? 'Please select a country first'
              : availableLaws.length === 0
              ? 'No laws available'
              : 'Select a law'}
          </MenuItem>
          {availableLaws.map((law) => (
            <MenuItem
              key={law.slug || law.id || law.lawShortName}
              value={law.lawShortName}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: alpha('#ffcf51', 0.08),
                },
                '&.Mui-selected': {
                  bgcolor: alpha('#ffcf51', 0.12),
                  '&:hover': {
                    bgcolor: alpha('#ffcf51', 0.16),
                  },
                },
              }}
            >
              <Box>
                <Typography
                  variant='body1'
                  sx={{
                    fontWeight:
                      law.lawShortName === currentLawShortName ? 600 : 400,
                    color: '#1f2937',
                    mb: 0.5,
                  }}
                >
                  {law.lawFullName}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Current Selection Display */}
      {/* {currentCountry && (
        <Box
          sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha('#e5e7eb', 0.5)}` }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <SwapIcon
              sx={{
                fontSize: 16,
                color: '#9ca3af',
                mr: 1,
              }}
            />
            <Typography
              variant='caption'
              sx={{
                color: '#6b7280',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Currently Viewing
            </Typography>
          </Box>
          <Typography
            variant='body2'
            sx={{
              color: '#374151',
              lineHeight: 1.4,
              fontWeight: 500,
              mb: currentLaw ? 1 : 0,
            }}
          >
            {currentCountry.countryName}
          </Typography>
          {currentLaw && (
            <Typography
              variant='body2'
              sx={{
                color: '#6b7280',
                lineHeight: 1.4,
                fontWeight: 400,
              }}
            >
              {currentLaw.lawFullName}
            </Typography>
          )}
        </Box>
      )} */}
    </LawSelector>
  )
}

const StyledAccordion = styled(Accordion)(
  ({ theme, level = 0, structuretype }) => {
    const getAccentColor = () => {
      const colorMap = {
        PART: '#6366f1',
        CHAPTER: '#3b82f6',
        SECTION: '#10b981',
        DEFAULT: '#f59e0b',
      }
      return colorMap[structuretype] || colorMap.DEFAULT
    }

    return {
      margin: theme.spacing(level === 0 ? 1.5 : 1.2, 0),
      borderRadius: '16px !important',
      overflow: 'hidden',
      background: level === 0 ? 'white' : 'rgba(248, 250, 252, 0.6)',
      border: `1px solid ${alpha(getAccentColor(), 0.1)}`,
      borderLeft: `4px solid ${getAccentColor()}`,
      boxShadow:
        level === 0
          ? '0 4px 20px rgba(0, 0, 0, 0.06)'
          : '0 2px 8px rgba(0, 0, 0, 0.03)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:before': {
        display: 'none',
      },
      '&:hover': {
        transform: level === 0 ? 'translateY(-4px)' : 'translateY(-2px)',
        boxShadow:
          level === 0
            ? '0 12px 40px rgba(0, 0, 0, 0.12)'
            : '0 8px 25px rgba(0, 0, 0, 0.08)',
        borderLeftColor: getAccentColor(),
      },
    }
  }
)

const StyledAccordionSummary = styled(AccordionSummary)(
  ({ theme, level = 0 }) => ({
    minHeight: level === 0 ? 64 : 56,
    padding: theme.spacing(0, level === 0 ? 2.5 : 2),
    '& .MuiAccordionSummary-content': {
      margin: theme.spacing(level === 0 ? 2 : 1.5, 0),
      alignItems: 'center',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: level === 0 ? '#374151' : '#6b7280',
      transition: 'transform 0.3s ease, color 0.3s ease',
    },
    '&:hover .MuiAccordionSummary-expandIconWrapper': {
      color: '#ffcf51',
    },
  })
)

const getResponsiveValue = (theme, values) => {
  return {
    [theme.breakpoints.down('sm')]: values.mobile,
    [theme.breakpoints.between('sm', 'md')]: values.tablet,
    [theme.breakpoints.up('md')]: values.desktop,
  }
}

const IconContainer = styled(Box)(
  ({ theme, structuretype = 'DEFAULT', size = 32 }) => {
    const colorMap = {
      PART: '#6366f1',
      CHAPTER: '#3b82f6',
      SECTION: '#10b981',
      ARTICLE: '#f59e0b',
      DEFAULT: '#f59e0b',
    }

    const color = colorMap[structuretype] || colorMap.DEFAULT

    return {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      borderRadius: theme.spacing(1),
      marginRight: theme.spacing(1.5),
      color: 'white',
      background: `linear-gradient(135deg, ${color} 0%, ${alpha(
        color,
        0.8
      )} 100%)`,
      boxShadow: `0 4px 12px ${alpha(color, 0.25)}`,
      transition: 'all 0.3s ease',
      flexShrink: 0,

      // Responsive sizing
      ...getResponsiveValue(theme, {
        mobile: {
          width: Math.max(24, size * 0.75),
          height: Math.max(24, size * 0.75),
          marginRight: theme.spacing(1),
          borderRadius: theme.spacing(0.75),
        },
        tablet: {
          width: Math.max(28, size * 0.875),
          height: Math.max(28, size * 0.875),
          marginRight: theme.spacing(1.25),
          borderRadius: theme.spacing(1),
        },
        desktop: {
          width: size,
          height: size,
          marginRight: theme.spacing(1.5),
          borderRadius: theme.spacing(1.5),
        },
      }),
    }
  }
)

const ArticleCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(226, 232, 240, 0.8)',
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(5px)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
    borderColor: '#ffcf51',
    background: 'white',
  },
}))

const ScrollToTopFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: 30,
  right: 16,
  zIndex: 1000,
  background: 'linear-gradient(135deg, #ffcf51 0%, #f59e0b 100%)',
  color: 'white',
  boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    transform: 'scale(1.1)',
  },
}))

// Enhanced Table of Contents with better scroll sync
const TableOfContentsTree = ({ structure, activeSection, onSectionClick }) => {
  const [expandedSections, setExpandedSections] = useState(() => {
    // Auto-expand sections that contain the active section
    const expanded = new Set()
    if (activeSection) {
      // Add logic to expand parent sections
      const findAndExpandParents = (items, path = []) => {
        items.forEach((item) => {
          const currentPath = [...path, generateSectionId(item)]
          if (activeSection.includes(generateSectionId(item))) {
            path.forEach((parentId) => expanded.add(parentId))
          }
          if (item.children) {
            findAndExpandParents(Object.values(item.children), currentPath)
          }
        })
      }
      findAndExpandParents(structure)
    }
    return expanded
  })

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections((prev) => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(sectionId)) {
        newExpanded.delete(sectionId)
      } else {
        newExpanded.add(sectionId)
      }
      return newExpanded
    })
  }, [])

  const getIcon = (type) => {
    const iconProps = { fontSize: 'small' }
    switch (type?.toUpperCase()) {
      case 'PART':
        return <PartIcon {...iconProps} />
      case 'CHAPTER':
        return <ChapterIcon {...iconProps} />
      case 'SECTION':
        return <SectionIcon {...iconProps} />
      default:
        return <MenuBookIcon {...iconProps} />
    }
  }

  const renderTocItem = (item, level = 0) => {
    const sectionId = generateSectionId(item)
    const hasChildren = item.children && Object.keys(item.children).length > 0
    const isExpanded = expandedSections.has(sectionId)
    const isActive = activeSection === sectionId

    return (
      <React.Fragment key={sectionId}>
        <ListItem
          button
          onClick={() => onSectionClick(sectionId)}
          sx={{
            pl: level * 2.5 + 1,
            py: 1,
            borderRadius: 2,
            mb: 0.5,
            bgcolor: isActive ? alpha('#ffcf51', 0.15) : 'transparent',
            borderLeft: isActive
              ? '3px solid #ffcf51'
              : '3px solid transparent',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: alpha('#ffcf51', 0.08),
              transform: 'translateX(4px)',
            },
          }}
        >
          <ListItemIcon
            sx={{ minWidth: 36, color: isActive ? '#ffcf51' : 'inherit' }}
          >
            {getIcon(item.type)}
          </ListItemIcon>
          <ListItemText
            primary={item.name}
            primaryTypographyProps={{
              fontSize: Math.max(0.75, 0.9 - level * 0.05) + 'rem',
              fontWeight: isActive ? 600 : 500,
              color: isActive ? '#1f2937' : '#4b5563',
              lineHeight: 1.4,
            }}
          />
          {hasChildren && (
            <Tooltip
              title={
                isExpanded
                  ? `Collapse ${item.type.toLowerCase()}`
                  : `Expand ${item.type.toLowerCase()}`
              }
              arrow
              placement='bottom-start'
            >
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation()
                  toggleSection(sectionId)
                }}
                sx={{
                  color: isActive ? '#ffcf51' : '#9ca3af',
                  '&:hover': { color: '#ffcf51' },
                }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Tooltip>
          )}
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout='auto' unmountOnExit>
            <List component='div' disablePadding>
              {Object.values(item.children).map((child) =>
                renderTocItem(child, level + 1)
              )}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    )
  }

  return (
    <List dense sx={{ py: 0 }}>
      {structure.map((item) => renderTocItem(item))}
    </List>
  )
}

// Enhanced Hierarchy Level with better intersection observer
const HierarchyLevel = ({
  structure,
  level = 0,
  lawShortName,
  onSectionInView,
}) => {
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const sectionRefs = useRef({})
  const observerRef = useRef()

  const getIcon = (type) => {
    switch (type?.toUpperCase()) {
      case 'PART':
        return <PartIcon />
      case 'CHAPTER':
        return <ChapterIcon />
      case 'SECTION':
        return <SectionIcon />
      default:
        return <MenuBookIcon />
    }
  }

  // Improved intersection observer with proper cleanup and throttling
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    const observerOptions = {
      root: null,
      rootMargin: '-10% 0px -60% 0px',
      threshold: [0, 0.1, 0.5, 1.0],
    }

    observerRef.current = new IntersectionObserver((entries) => {
      // Find the entry with the highest intersection ratio
      let mostVisible = null
      let highestRatio = 0

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
          highestRatio = entry.intersectionRatio
          mostVisible = entry
        }
      })

      if (mostVisible) {
        const sectionId = mostVisible.target.id
        if (sectionId) {
          onSectionInView(sectionId)
        }
      }
    }, observerOptions)

    // Observe all section elements
    Object.values(sectionRefs.current).forEach((element) => {
      if (element && observerRef.current) {
        observerRef.current.observe(element)
      }
    })

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [structure, onSectionInView])

  const sortedStructure = useMemo(() => {
    return [...structure].sort((a, b) => {
      const numA = parseInt(a.number)
      const numB = parseInt(b.number)
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB
      }
      return a.number.localeCompare(b.number)
    })
  }, [structure])

  return (
    <Box sx={{ pl: level > 0 ? 1.5 : 0 }}>
      {sortedStructure.map((item, index) => {
        const hasChildren =
          item.children && Object.keys(item.children).length > 0
        const hasArticles = item.articles && item.articles.length > 0
        const sectionId = generateSectionId(item)

        return (
          <StyledAccordion
            level={level}
            structuretype={item.type}
            defaultExpanded={level === 0}
            ref={(el) => (sectionRefs.current[sectionId] = el)}
            id={sectionId}
            key={`${item.type}-${item.number}-${index}`}
          >
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              level={level}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                <IconContainer structuretype={item.type}>
                  {getIcon(item.type)}
                </IconContainer>

                <Box sx={{ flexGrow: 1 }}>
                  {/* First row - Type and Number */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant={level === 0 ? 'h6' : 'subtitle1'}
                      component='h3'
                      sx={{
                        fontWeight: 700,
                        fontSize:
                          level === 0
                            ? isMobileScreen
                              ? '1.1rem'
                              : '1.25rem'
                            : isMobileScreen
                            ? '0.95rem'
                            : '1rem',
                        color: '#1f2937',
                        lineHeight: 1.2,
                      }}
                    >
                      {(() => {
                        const parts = item.name.split(' - ')
                        if (parts.length > 1) {
                          return parts[0]
                        }
                        return `${item.type} ${item.number}`
                      })()}
                    </Typography>

                    <CopyLinkButton sectionId={sectionId} />
                  </Box>

                  {/* Second row - Descriptive name */}
                  <Typography
                    variant={level === 0 ? 'subtitle1' : 'body2'}
                    component='h4'
                    sx={{
                      fontWeight: 500,
                      fontSize:
                        level === 0
                          ? isMobileScreen
                            ? '0.95rem'
                            : '1rem'
                          : isMobileScreen
                          ? '0.85rem'
                          : '0.9rem',
                      color: '#4b5563',
                      lineHeight: 1.3,
                      mb: 1.5,
                    }}
                  >
                    {(() => {
                      const parts = item.name.split(' - ')
                      if (parts.length > 1) {
                        return parts.slice(1).join(' - ')
                      }
                      return item.name
                    })()}
                  </Typography>

                  {/* Chips row */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {hasArticles && hasChildren && (
                      <Chip
                        icon={<ArticleIcon fontSize='small' />}
                        label={`${item.articles.length} Article${
                          item.articles.length !== 1 ? 's' : ''
                        }`}
                        size='small'
                        sx={{
                          fontSize: '0.7rem',
                          height: 22,
                          fontWeight: 600,
                          bgcolor: alpha('#3b82f6', 0.1),
                          color: '#1e40af',
                          border: '1px solid',
                          borderColor: alpha('#3b82f6', 0.2),
                        }}
                      />
                    )}

                    {hasChildren && (
                      <Chip
                        icon={<MenuBookIcon fontSize='small' />}
                        label={`${
                          Object.keys(item.children).length
                        } Sub-section${
                          Object.keys(item.children).length !== 1 ? 's' : ''
                        }`}
                        size='small'
                        sx={{
                          fontSize: '0.7rem',
                          height: 22,
                          fontWeight: 600,
                          bgcolor: alpha('#10b981', 0.1),
                          color: '#047857',
                          border: '1px solid',
                          borderColor: alpha('#10b981', 0.2),
                        }}
                      />
                    )}

                    <Chip
                      label={`Total: ${item.articleCount} article${
                        item.articleCount !== 1 ? 's' : ''
                      }`}
                      size='small'
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        bgcolor: alpha('#ffcf51', 0.15),
                        color: '#b45309',
                        border: '1px solid',
                        borderColor: alpha('#ffcf51', 0.3),
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </StyledAccordionSummary>
            <AccordionDetails sx={{ pt: 2, pb: 3, px: level === 0 ? 2.5 : 2 }}>
              {/* Articles Section */}
              {hasArticles && (
                <Box sx={{ mb: hasChildren ? 3 : 0 }}>
                  <Typography
                    variant='h6'
                    component='h4'
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      color: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                      '&::before': {
                        content: '""',
                        width: 4,
                        height: 28,
                        backgroundColor: '#ffcf51',
                        borderRadius: 2,
                        marginRight: 2,
                      },
                    }}
                  >
                    <ArticleIcon sx={{ mr: 1.5 }} />
                    Articles
                  </Typography>

                  <Grid container spacing={3}>
                    {item.articles.map((article, idx) => (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={4}
                        key={`article-${idx}-${article.slug}`}
                      >
                        <Zoom in={true} timeout={300 + idx * 50}>
                          <ArticleCard
                            onClick={() => {
                              const newWindow = window.open(
                                `/articles/${article.slug}`,
                                '_blank'
                              )
                              newWindow.focus()
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  mb: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <IconContainer
                                    structuretype='ARTICLE'
                                    size={40}
                                    sx={{ mr: 1.5 }}
                                  >
                                    <ArticleIcon fontSize='small' />
                                  </IconContainer>
                                  <Typography
                                    variant='subtitle2'
                                    sx={{
                                      fontWeight: 700,
                                      color: '#1f2937',
                                    }}
                                  >
                                    Article {article.number}
                                  </Typography>
                                </Box>

                                <Tooltip title='Open in new tab' arrow>
                                  <IconButton
                                    size='small'
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      window.open(
                                        `/articles/${article.slug}`,
                                        '_blank'
                                      )
                                    }}
                                    sx={{
                                      color: '#9ca3af',
                                      '&:hover': {
                                        color: '#ffcf51',
                                        bgcolor: alpha('#ffcf51', 0.1),
                                      },
                                    }}
                                  >
                                    <OpenInNewRoundedIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              </Box>

                              <Typography
                                variant='body2'
                                sx={{
                                  color: '#6b7280',
                                  lineHeight: 1.6,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {article.title.replace(
                                  `Article ${article.number} - `,
                                  ''
                                )}
                              </Typography>
                            </CardContent>
                          </ArticleCard>
                        </Zoom>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Sub-sections */}
              {hasChildren && (
                <Box>
                  <Typography
                    variant='h6'
                    component='h4'
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      color: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                      '&::before': {
                        content: '""',
                        width: 4,
                        height: 28,
                        backgroundColor: '#10b981',
                        borderRadius: 2,
                        marginRight: 2,
                      },
                    }}
                  >
                    <MenuBookIcon sx={{ mr: 1.5 }} />
                    {(() => {
                      const firstChildType = Object.values(item.children)[0]
                        ?.type
                      switch (firstChildType) {
                        case 'CHAPTER':
                          return 'Chapters'
                        case 'SECTION':
                          return 'Sections'
                        case 'PART':
                          return 'Parts'
                        default:
                          return 'Sub-sections'
                      }
                    })()}
                  </Typography>

                  <HierarchyLevel
                    structure={Object.values(item.children)}
                    level={level + 1}
                    lawShortName={lawShortName}
                    onSectionInView={onSectionInView}
                  />
                </Box>
              )}
            </AccordionDetails>
          </StyledAccordion>
        )
      })}
    </Box>
  )
}

// Main Component with improved scroll handling and law selection
const HierarchicalLawStructure = () => {
  const theme = useTheme()
  const { country, lawShortName: urlLawShortName } = useParams()
  const navigate = useNavigate()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'))

  // Use the context
  const {
    countriesWithLaws,
    selectedCountry,
    setSelectedCountry,
    availableLaws,
    setAvailableLaws,
  } = useCountryLaw()

  const [loading, setLoading] = useState(true)
  const [hierarchyLoading, setHierarchyLoading] = useState(false)
  const [error, setError] = useState(null)
  const [validationError, setValidationError] = useState(null)
  const [hierarchyData, setHierarchyData] = useState(null)
  const [activeSection, setActiveSection] = useState('')
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [tocOpen, setTocOpen] = useState(false)
  const [currentLawShortName, setCurrentLawShortName] = useState('')

  // Enhanced scroll tracking
  useEffect(() => {
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setShowScrollTop(window.pageYOffset > 300)
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSectionInView = useCallback((sectionId) => {
    setActiveSection(sectionId)
  }, [])

  const handleTocSectionClick = useCallback(
    (sectionId) => {
      const element = document.getElementById(sectionId)
      if (element) {
        const headerOffset = 80
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      }
      if (isMobileScreen) {
        setTocOpen(false)
      }
    },
    [isMobileScreen]
  )

  // Handle country change (manual selection)
  const handleCountryChange = useCallback(
    (newCountry) => {
      if (newCountry !== selectedCountry) {
        const targetCountryData = countriesWithLaws.find(
          (c) => c.countryName === newCountry
        )

        if (targetCountryData) {
          setSelectedCountry(newCountry)
          setAvailableLaws(targetCountryData.laws || [])

          // Reset law selection and hierarchy data
          setCurrentLawShortName('')
          setHierarchyData(null)
          setActiveSection('')
          setError(null)
          setValidationError(null)

          // Navigate to first law of the new country
          if (targetCountryData.laws?.length > 0) {
            const firstLaw = targetCountryData.laws[0]
            navigate(`/laws/${newCountry}/${firstLaw.lawShortName}`)
          }
        }
      }
    },
    [
      selectedCountry,
      countriesWithLaws,
      setSelectedCountry,
      setAvailableLaws,
      navigate,
    ]
  )

  // Handle law change
  const handleLawChange = useCallback(
    (newLawShortName) => {
      if (newLawShortName !== currentLawShortName) {
        setCurrentLawShortName(newLawShortName)

        // Reset states
        setHierarchyData(null)
        setActiveSection('')
        setError(null)
        setValidationError(null)

        // Update URL with both country and law
        if (newLawShortName && selectedCountry) {
          navigate(`/laws/${selectedCountry}/${newLawShortName}`)
        }
      }
    },
    [currentLawShortName, selectedCountry, navigate]
  )

  // Main validation and initialization logic
  useEffect(() => {
    if (countriesWithLaws.length === 0) return // Wait for countries to load

    let targetCountry = null
    let targetLaw = null

    // Step 1: Validate and determine target country
    if (country) {
      // Check if URL country exists in countriesWithLaws
      const countryExists = countriesWithLaws.find(
        (c) => c.countryName.toLowerCase() === country.toLowerCase()
      )

      if (countryExists) {
        targetCountry = countryExists
      } else {
        // URL country not found, use first available country
        targetCountry = countriesWithLaws[0]
      }
    } else {
      // No country in URL, use first available country
      targetCountry = countriesWithLaws[0]
    }

    // Step 2: Update selected country if different
    if (targetCountry && selectedCountry !== targetCountry.countryName) {
      setSelectedCountry(targetCountry.countryName)
      setAvailableLaws(targetCountry.laws || [])
    }

    // Step 3: Validate and determine target law
    if (targetCountry?.laws?.length > 0) {
      if (urlLawShortName) {
        // Check if URL law exists in target country's laws
        const lawExists = targetCountry.laws.find(
          (law) =>
            law.lawShortName.toLowerCase() === urlLawShortName.toLowerCase()
        )

        if (lawExists) {
          targetLaw = lawExists
        } else {
          // URL law not found in target country, use first law
          targetLaw = targetCountry.laws[0]
        }
      } else {
        // No law in URL, use first law
        targetLaw = targetCountry.laws[0]
      }

      // Step 4: Update current law and navigate to correct URL
      if (targetLaw && currentLawShortName !== targetLaw.lawShortName) {
        setCurrentLawShortName(targetLaw.lawShortName)

        // Update URL to reflect the correct country and law
        const correctUrl = `/laws/${targetCountry.countryName}/${targetLaw.lawShortName}`
        const currentUrl = `/laws/${country || ''}/${
          urlLawShortName || ''
        }`.replace('//', '/')

        if (correctUrl !== currentUrl) {
          navigate(correctUrl, { replace: true })
        }
      }
    } else {
      // No laws available for target country
      setCurrentLawShortName('')
      setHierarchyData(null)
      setAvailableLaws([])
    }

    // Clear any previous validation errors
    setValidationError(null)
    setError(null)
  }, [
    country,
    urlLawShortName,
    countriesWithLaws,
    selectedCountry,
    currentLawShortName,
    setSelectedCountry,
    setAvailableLaws,
    navigate,
  ])

  // Fetch law hierarchy when law changes
  useEffect(() => {
    const fetchLawHierarchy = async () => {
      if (!selectedCountry || !currentLawShortName) {
        setLoading(false)
        return
      }

      try {
        setHierarchyLoading(true)
        setError(null)
        setValidationError(null)

        const response = await apiService.get(
          `/api/v1/laws/${selectedCountry}/${currentLawShortName}/hierarchy`
        )
        setHierarchyData(response)
      } catch (err) {
        console.error('Error fetching law hierarchy:', err)
        setError(
          err.response?.data?.message ||
            'Failed to load law structure. Please try again.'
        )
      } finally {
        setHierarchyLoading(false)
        setLoading(false)
      }
    }

    if (currentLawShortName && selectedCountry) {
      fetchLawHierarchy()
    } else {
      setLoading(false)
    }
  }, [selectedCountry, currentLawShortName])

  // Handle URL hash navigation
  useEffect(() => {
    const hash = window.location.hash.substring(1)
    if (hash && hierarchyData && !hierarchyLoading) {
      const timer = setTimeout(() => {
        const element = document.getElementById(hash)
        if (element) {
          const headerOffset = 80
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition =
            elementPosition + window.pageYOffset - headerOffset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          })
        }
      }, 800)

      return () => clearTimeout(timer)
    }
  }, [hierarchyData, hierarchyLoading])

  // Loading state for countries
  const isCountriesLoading = loading && countriesWithLaws.length === 0

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        minHeight: '100vh',
      }}
    >
      <Container maxWidth='xl' sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Law Selector and Table of Contents - Desktop */}
          {!isMobileScreen && (
            <Grid item md={3}>
              {/* Country and Law Selector */}
              <LawSelectorComponent
                availableLaws={availableLaws}
                countriesWithLaws={countriesWithLaws}
                currentLawShortName={currentLawShortName}
                selectedCountry={selectedCountry}
                onLawChange={handleLawChange}
                onCountryChange={handleCountryChange}
                loading={isCountriesLoading || hierarchyLoading}
              />

              {/* Table of Contents */}
              {hierarchyData && (
                <TableOfContents>
                  <Typography
                    variant='h6'
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <TocRoundedIcon sx={{ mr: 1, color: '#ffcf51' }} />
                    Contents
                  </Typography>
                  <TableOfContentsTree
                    structure={hierarchyData.structure}
                    activeSection={activeSection}
                    onSectionClick={handleTocSectionClick}
                  />
                </TableOfContents>
              )}
            </Grid>
          )}

          {/* Main Content */}
          <Grid item xs={12} md={isMobileScreen ? 12 : 9}>
            {/* Mobile Country and Law Selector */}
            {isMobileScreen && (
              <Box sx={{ mb: 3 }}>
                <LawSelectorComponent
                  availableLaws={availableLaws}
                  countriesWithLaws={countriesWithLaws}
                  currentLawShortName={currentLawShortName}
                  selectedCountry={selectedCountry}
                  onLawChange={handleLawChange}
                  onCountryChange={handleCountryChange}
                  loading={isCountriesLoading || hierarchyLoading}
                />
              </Box>
            )}

            {/* Validation Error */}
            {validationError && (
              <Alert
                severity='error'
                sx={{
                  mb: 4,
                  borderRadius: 2,
                  border: '1px solid #fecaca',
                  bgcolor: '#fef2f2',
                }}
              >
                {validationError}
              </Alert>
            )}

            <Box id='top' sx={{ mb: 6 }}>
              <Typography
                variant='h4'
                component='h1'
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  background:
                    'linear-gradient(135deg, #1f2937 0%, #4b5563 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.2,
                }}
              >
                {hierarchyData?.lawFullName ||
                  currentLawShortName?.toUpperCase() ||
                  (selectedCountry ? `${selectedCountry} Laws` : 'Select Laws')}
              </Typography>

              <Typography
                variant='subtitle1'
                color='text.secondary'
                sx={{
                  maxWidth: '600px',
                  lineHeight: 1.6,
                  fontWeight: 400,
                  color: '#6b7280',
                }}
              >
                {selectedCountry && currentLawShortName
                  ? 'Navigate through the complete legal structure with ease'
                  : selectedCountry
                  ? 'Please select a law to view its structure'
                  : 'Please select a country and law to view the legal structure'}
              </Typography>

              {/* Enhanced Statistics */}
              {hierarchyData && (
                <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Chip
                    icon={<PartIcon fontSize='small' />}
                    label={`${
                      hierarchyData.structure?.length || 0
                    } Main Sections`}
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha('#6366f1', 0.1),
                      color: '#4338ca',
                      border: '1px solid',
                      borderColor: alpha('#6366f1', 0.2),
                    }}
                  />
                  <Chip
                    icon={<ArticleIcon fontSize='small' />}
                    label={`${
                      hierarchyData.structure?.reduce(
                        (total, section) => total + (section.articleCount || 0),
                        0
                      ) || 0
                    } Total Articles`}
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha('#f59e0b', 0.1),
                      color: '#d97706',
                      border: '1px solid',
                      borderColor: alpha('#f59e0b', 0.2),
                    }}
                  />
                  <Chip
                    icon={<CountryIcon fontSize='small' />}
                    label={`${selectedCountry?.toUpperCase()} Law`}
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha('#10b981', 0.1),
                      color: '#047857',
                      border: '1px solid',
                      borderColor: alpha('#10b981', 0.2),
                    }}
                  />
                </Box>
              )}
            </Box>

            {isCountriesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                <CircularProgress sx={{ color: '#ffcf51' }} size={60} />
                <Typography sx={{ ml: 2, color: '#6b7280' }}>
                  Loading available countries...
                </Typography>
              </Box>
            ) : hierarchyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                <CircularProgress sx={{ color: '#ffcf51' }} size={60} />
                <Typography sx={{ ml: 2, color: '#6b7280' }}>
                  Loading law structure...
                </Typography>
              </Box>
            ) : error ? (
              <Alert
                severity='error'
                sx={{
                  my: 4,
                  borderRadius: 2,
                  border: '1px solid #fecaca',
                  bgcolor: '#fef2f2',
                }}
              >
                {error}
              </Alert>
            ) : !selectedCountry || !currentLawShortName ? (
              <Alert
                severity='info'
                sx={{
                  my: 4,
                  borderRadius: 2,
                  border: '1px solid #bfdbfe',
                  bgcolor: '#eff6ff',
                }}
              >
                Loading law structure...
              </Alert>
            ) : !hierarchyData ||
              !hierarchyData.structure ||
              hierarchyData.structure.length === 0 ? (
              <Alert
                severity='info'
                sx={{
                  my: 4,
                  borderRadius: 2,
                  border: '1px solid #bfdbfe',
                  bgcolor: '#eff6ff',
                }}
              >
                No structure information available for this law.
              </Alert>
            ) : (
              <HierarchyLevel
                structure={hierarchyData.structure}
                lawShortName={currentLawShortName}
                onSectionInView={handleSectionInView}
              />
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Mobile Table of Contents */}
      {isMobileScreen && hierarchyData && (
        <>
          <Fab
            sx={{
              position: 'fixed',
              bottom: 100,
              right: 16,
              bgcolor: '#ffcf51',
              color: 'white',
              boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)',
              '&:hover': {
                bgcolor: '#f59e0b',
                transform: 'scale(1.1)',
              },
            }}
            onClick={() => setTocOpen(true)}
          >
            <TocRoundedIcon />
          </Fab>

          <Drawer
            anchor='right'
            open={tocOpen}
            onClose={() => setTocOpen(false)}
            PaperProps={{
              sx: {
                width: '85%',
                maxWidth: 380,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
              },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, color: '#1f2937' }}
                >
                  Contents
                </Typography>
                <IconButton
                  onClick={() => setTocOpen(false)}
                  sx={{
                    color: '#9ca3af',
                    '&:hover': { color: '#ffcf51' },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Typography
                variant='body2'
                sx={{
                  mb: 3,
                  color: '#6b7280',
                  lineHeight: 1.5,
                }}
              >
                {hierarchyData?.lawFullName}
              </Typography>

              <TableOfContentsTree
                structure={hierarchyData.structure}
                activeSection={activeSection}
                onSectionClick={handleTocSectionClick}
              />
            </Box>
          </Drawer>
        </>
      )}

      {/* Scroll to Top */}
      <Zoom in={showScrollTop}>
        <ScrollToTopFab
          onClick={scrollToTop}
          fontSize={isMobileScreen ? 'small' : 'medium'}
        >
          <ScrollTopIcon />
        </ScrollToTopFab>
      </Zoom>
    </Box>
  )
}

export default HierarchicalLawStructure